<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import subDays from 'date-fns/subDays';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import V4Button from 'dashboard/components-next/button/Button.vue';
import format from 'date-fns/format';
import fromUnixTime from 'date-fns/fromUnixTime';

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();

const accountLabels = computed(() => getters['labels/getLabels'].value);
const selectedLabels = ref([]);
const dateRange = ref([subDays(new Date(), 6), new Date()]);
const selectedDateRange = ref(DATE_RANGE_TYPES.LAST_7_DAYS);
const isDownloading = ref(false);

const onDateRangeChange = value => {
  const [startDate, endDate, rangeType] = value;
  dateRange.value = [startDate, endDate];
  selectedDateRange.value = rangeType || DATE_RANGE_TYPES.CUSTOM_RANGE;
};

const downloadReport = async () => {
  const from = getUnixStartOfDay(dateRange.value[0]);
  const to = getUnixEndOfDay(dateRange.value[1]);
  const fileName = `conversas-por-etiqueta-${format(fromUnixTime(to), 'dd-MM-yyyy')}.xlsx`;
  isDownloading.value = true;
  try {
    await store.dispatch('downloadLabelConversationsReport', {
      from,
      to,
      labels: selectedLabels.value,
      fileName,
    });
  } catch (error) {
    useAlert(t('REPORT.DATA_FETCHING_FAILED'));
  } finally {
    isDownloading.value = false;
  }
};
</script>

<template>
  <div class="p-4 mt-4 border rounded-lg border-n-weak">
    <h4 class="mb-1 text-base font-medium text-n-slate-12">
      {{ t('LABEL_REPORTS.CONVERSATIONS_EXPORT.TITLE') }}
    </h4>
    <p class="mb-4 text-sm text-n-slate-11">
      {{ t('LABEL_REPORTS.CONVERSATIONS_EXPORT.DESCRIPTION') }}
    </p>
    <div class="flex flex-col gap-4">
      <WootDatePicker
        v-model:date-range="dateRange"
        v-model:range-type="selectedDateRange"
        @date-range-changed="onDateRangeChange"
      />
      <div class="flex flex-wrap gap-2">
        <label
          v-for="label in accountLabels"
          :key="label.id"
          class="flex items-center gap-1.5 py-1 px-2 rounded-lg border border-n-weak cursor-pointer hover:bg-n-slate-3"
        >
          <input
            v-model="selectedLabels"
            type="checkbox"
            :value="label.title"
          />
          <span class="text-sm text-n-slate-12">{{ label.title }}</span>
        </label>
        <p v-if="!accountLabels.length" class="text-sm text-n-slate-11">
          {{ t('LABEL_REPORTS.CONVERSATIONS_EXPORT.NO_LABELS') }}
        </p>
      </div>
      <V4Button
        :label="t('LABEL_REPORTS.CONVERSATIONS_EXPORT.DOWNLOAD')"
        icon="i-ph-download-simple"
        size="sm"
        class="self-start"
        :is-loading="isDownloading"
        @click="downloadReport"
      />
    </div>
  </div>
</template>
